import {
  goBack as goBackConnected,
  Push,
  push as pushConnected,
  Replace,
  replace as replaceConnected,
} from 'connected-react-router';
import React, { createContext } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import { paramsToSearch, searchToParams } from 'helpers/router';
import { usePersistentValue } from 'hooks/usePersistentValue';
import { AnyRecord, LocationState, Params } from 'types';

type PushPageFn = (page?: string, params?: Params) => void;
type ReplacePageFn = (page?: string, params?: Params) => void;
type PushFn = (...args: Parameters<Push>) => void;
type ReplaceFn = (...args: Parameters<Replace>) => void;
type GoBackFn = () => void;
type RemoveParams = (keys: Array<string>, timeout?: number) => void;
type UpdateParams = (paramsToUpdate: AnyRecord, timeout?: number) => void;

export type RouterContextValue = {
  params: Params;
  pushPage: PushPageFn;
  replacePage: ReplacePageFn;
  push: PushFn;
  replace: ReplaceFn;
  goBack: GoBackFn;
  removeParams: RemoveParams;
  updateParams: UpdateParams;
  routes: {
    scene: string;
    page: string;
  };
};

const initialValue: RouterContextValue = {
  params: {},
  pushPage: () => {}, // eslint-disable-line
  replacePage: () => {}, // eslint-disable-line
  push: () => {}, // eslint-disable-line
  replace: () => {}, // eslint-disable-line
  goBack: () => {}, // eslint-disable-line
  removeParams: () => {}, // eslint-disable-line
  updateParams: () => {}, // eslint-disable-line
  routes: {
    scene: '',
    page: '',
  },
};

export const RouterContext = createContext<RouterContextValue>(initialValue);

export const RouterProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation<LocationState>();
  const params = searchToParams(search.slice(1)) as Params;
  const refParams = usePersistentValue(params, [params]);

  const push: PushFn = (...args) => dispatch(pushConnected(...args));
  const goBack: GoBackFn = () => dispatch(goBackConnected());
  const replace: ReplaceFn = (...args) => dispatch(replaceConnected(...args));

  const pathNameSplit = pathname.split('/');
  const scenneRoute = pathNameSplit?.[1] && `/${pathNameSplit?.[1]}`;
  const pageRoute = pathNameSplit?.[2] && `/${pathNameSplit?.[2]}`;

  const pushPage: PushPageFn = (newPageRoute, newParams) =>
    push({
      pathname: `${scenneRoute}${newPageRoute || pageRoute}`,
      search: paramsToSearch(newParams || {}),
    });

  const replacePage: ReplacePageFn = (newPageRoute, newParams) =>
    replace({
      pathname: `${scenneRoute}${newPageRoute || pageRoute}`,
      search: paramsToSearch(newParams || {}),
    });

  const removeParams: RemoveParams = (keys, timeout) => {
    const handle = () =>
      pushPage(
        undefined,
        Object.keys(refParams.current).reduce(
          (acc, key) => (keys.includes(key) ? acc : { ...acc, [key]: refParams.current[key] }),
          {}
        )
      );

    if (timeout) {
      setTimeout(handle, timeout);
      return;
    }
    handle();
  };

  const updateParams: UpdateParams = (newParams, timeout) => {
    const handle = () => pushPage(undefined, { ...params, ...newParams });
    if (timeout) {
      setTimeout(handle, timeout);
      return;
    }
    handle();
  };

  const value = {
    params,
    pushPage,
    replacePage,
    push,
    replace,
    goBack,
    routes: {
      scene: scenneRoute,
      page: pageRoute,
    },
    removeParams,
    updateParams,
  };
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
};
